import { modalService } from "@whatslly/ui-components/modals";
import styles from "./Modals.module.scss";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { getJSON, postJSON } from "@whatslly/commons/apiUtils";
import { inject, injectable } from "inversify";
import { Button } from "@whatslly/ui-components/v2/Button";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { useTranslation } from "react-i18next";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import type { CRMServerToServerSettings } from "@whatslly/types/messages-server/destinations";
import { GenerateCertificateResponse, GenerateCertificateResponseSuccess, DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import pick from "lodash/pick";
import { observer } from "mobx-react-lite";
import { FormCheckBox } from "@whatslly/ui-components/v2/FormElements/FormCheckBox";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import { parseOrReturnError } from "@whatslly/commons/parseOrReturnError";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { ServerToServerBodyResponse } from "@whatslly/types/AuthBodyRequest";
import { downloadBlob } from "@whatslly/ui-components/componentsUtils";
import { TFunction } from "i18next";
import { makeObservable, observable, action } from "mobx";
import type { IOrganizationStore } from "../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../services/IOrganizationStore";
import type { DestinationFormProps, ExtractDestination } from "../utils/DestinationsListTypes";
import classNames from "classnames";

@injectable()
class ServerToServerStore {
  generateCertificatePromise: IPromiseBasedObservable<GenerateCertificateResponse | void> | undefined;
  generateServerToServerEtokenPromise: IPromiseBasedObservable<ServerToServerBodyResponse | void> | undefined;

  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable(this, {
      generateCertificatePromise: observable,
      generateServerToServerEtokenPromise: observable,
      generateCertificate: action,
      generateSererToServerEtoken: action,
    });
  }

  async generateCertificate(t: TFunction) {
    this.generateCertificatePromise = fromPromise(
      getJSON({
        urlSuffix: `/message/destinations/${await this.orgStore.orgId}/generate-certificate`,
        etoken: this.userStore.etoken ?? "",
      })
        .then((r) => parseOrReturnError<GenerateCertificateResponse>(r, this.logger))
        .then((r) => {
          if (r.success) {
            return r;
          }
          modalService.openModal(Toast, { text: t("destinations_page.generate_certificate_failed_toast"), type: "error" });
        })
        .catch(() => {
          modalService.openModal(Toast, { text: t("destinations_page.generate_certificate_failed_toast"), type: "error" });
        })
    );
  }
  generateSererToServerEtoken(
    t: TFunction,
    {
      serverUrl,
      privateKey,
      connectedAppConsumerKey,
      tenantId,
      certificate,
    }: {
      serverUrl?: string;
      tenantId?: string;
      connectedAppConsumerKey: string;
      privateKey: string;
      certificate: string;
    }
  ) {
    this.generateServerToServerEtokenPromise = fromPromise(
      (async () => {
        try {
          const res = await postJSON({
            urlSuffix: `/dynamics/auth/server-to-server`,
            body: JSON.stringify({
              privateKey,
              certificate,
              connectedAppConsumerKey,
              tenantId,
              serverUrl,
            }),
          }).then((r) => parseOrReturnError<ServerToServerBodyResponse>(r, this.logger));
          if (res.success) {
            return res;
          }
          modalService.openModal(Toast, { text: t("destinations_page.test_connection_failed_toast"), type: "error" });
        } catch {
          modalService.openModal(Toast, { text: t("destinations_page.test_connection_failed_toast"), type: "error" });
        }
      })()
    );
  }
  downloadCertificate(certificate: string | undefined) {
    if (certificate) {
      downloadBlob(atob(certificate), "tuvis-cert.pem", "application/x-pem-file");
      return;
    }
    if (this.generateCertificatePromise?.state === "fulfilled" && this.generateCertificatePromise.value?.success === true) {
      downloadBlob(atob(this.generateCertificatePromise.value.certificate), "tuvis-cert.pem", "application/x-pem-file");
    }
  }
}

type ServerToServerFormDataTypeFields = "serverUrl" | "connectedAppConsumerKey" | "tenantId" | "storeMedia";
type ServerToServerFormDataType = Pick<CRMServerToServerSettings, ServerToServerFormDataTypeFields>;

const ServerToServerTab: React.FC<DestinationFormProps<"dynamics"> & { state: ServerToServerStore }> = observer(
  ({ onSubmitForm, settings, state }) => {
    const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
    const formikContext = useRef<FormikProps<ServerToServerFormDataType>>(null);
    const { t } = useTranslation();

    const validate = async (values: ServerToServerFormDataType) => {
      const errors: FormikErrors<PartialRecord<keyof ServerToServerFormDataType, string>> = {};
      if (!values.serverUrl) errors.serverUrl = t("field_is_required");
      if (!values.connectedAppConsumerKey) errors.connectedAppConsumerKey = t("field_is_required");
      if (!values.tenantId) errors.tenantId = t("field_is_required");

      setFormikErrorHasErrors(Object.keys(errors).length > 0);
      return errors;
    };

    const isTestSubmitDisabled = useMemo(() => {
      const certFromServer = state.generateCertificatePromise?.state === "fulfilled" && state.generateCertificatePromise?.value?.success;
      const certFromSettings = settings?.type === "server-to-server" && settings.certificate;
      return (!certFromServer && !certFromSettings) || formikErrorHasErrors;
    }, [state.generateCertificatePromise?.state, formikErrorHasErrors]);

    const getFormValues = () => {
      return (formikContext.current?.values ?? {}) as ServerToServerFormDataType;
    };

    const getCertData = useCallback(() => {
      return state.generateCertificatePromise?.state === "fulfilled" && state.generateCertificatePromise.value?.success
        ? pick<GenerateCertificateResponseSuccess, "privateKey" | "certificate">(state.generateCertificatePromise.value ?? {}, [
            "privateKey",
            "certificate",
          ])
        : {
            certificate: settings?.type === "server-to-server" && settings.certificate ? settings.certificate : "",
            privateKey: settings?.type === "server-to-server" && settings.privateKey ? settings.privateKey : "",
          };
    }, [state.generateCertificatePromise?.state, settings]);

    const getSubmitData = useCallback((): ExtractDestination<"dynamics"> => {
      const certData = getCertData();
      return {
        type: "dynamics" as const,
        integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
        settings: {
          type: "server-to-server",
          etoken:
            state.generateServerToServerEtokenPromise?.state === "fulfilled" && state.generateServerToServerEtokenPromise.value?.success
              ? state.generateServerToServerEtokenPromise.value.etoken
              : "",
          ...getFormValues(),
          ...certData,
        },
      };
    }, [state.generateCertificatePromise?.state]);

    useEffect(() => {
      validate(getFormValues());
    }, []);

    const onSubmit = useCallback(async () => {
      state.generateSererToServerEtoken(t, {
        ...getCertData(),
        ...getFormValues(),
      });
      const res = await state.generateServerToServerEtokenPromise;
      if (res?.success) {
        onSubmitForm(getSubmitData());
      }
    }, []);

    return (
      <div>
        <div className={styles.serverToServer}>
          {(settings?.type === "server-to-server" && settings.certificate) ||
          (state.generateCertificatePromise?.state === "fulfilled" && state.generateCertificatePromise.value?.success === true) ? (
            <Button
              testId={"download-certificate-button"}
              onClick={() => state.downloadCertificate(settings?.type === "server-to-server" ? settings.certificate : undefined)}
              paddingInline={"small"}
            >
              {t("destinations_page.download_certificate")}
            </Button>
          ) : (
            <Button
              testId={"generate-certificate-button"}
              isSubmitting={state.generateCertificatePromise?.state === "pending"}
              disabled={state.generateCertificatePromise?.state === "pending"}
              onClick={() => state.generateCertificate(t)}
              paddingInline={"small"}
            >
              {t("destinations_page.generate_certificate")}
            </Button>
          )}
        </div>
        <FormElement
          initialValues={
            settings && settings.type === "server-to-server"
              ? pick<CRMServerToServerSettings, ServerToServerFormDataTypeFields>(settings, [
                  "serverUrl",
                  "connectedAppConsumerKey",
                  "tenantId",
                  "storeMedia",
                ])
              : { serverUrl: "", connectedAppConsumerKey: "", tenantId: "", storeMedia: false }
          }
          validate={validate}
          onSubmit={onSubmit}
          formikRef={formikContext}
        >
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.server_url")} required />
            <FormTextInput name={"serverUrl"} paddingInline="none" placeholder={`https://*.dynamics.com/*`} />
          </div>
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.client_id")} required />
            <FormTextInput name={"connectedAppConsumerKey"} paddingInline="none" />
          </div>
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.tenant_id")} required />
            <FormTextInput name={"tenantId"} paddingInline="none" />
          </div>
          <div className={classNames(styles.field, styles.fieldCheckbox)}>
            <Label className={styles.filedLabel} text={t("destinations_page.store_media")} />
            <FormCheckBox name={"storeMedia"} paddingInline="none" />
          </div>
        </FormElement>
        <ModalButtons
          testId={`sf-save-button`}
          submitText={
            state.generateServerToServerEtokenPromise?.state === "pending" ? t("destinations_page.test_connection") : t("destinations_page.save")
          }
          isSubmitDisabled={isTestSubmitDisabled || state.generateServerToServerEtokenPromise?.state === "pending"}
          onSubmit={() => formikContext.current?.submitForm()}
          hideCancel={true}
        />
      </div>
    );
  }
);

export const DynamicsModalForm: React.FC<DestinationFormProps<"dynamics">> = observer(
  ({ onSubmitForm, destinationType, settings, extraSettings, batchJob }) => {
    const state = useLocalStoreWithInjectedDependencies(ServerToServerStore);

    return (
      <ServerToServerTab
        onSubmitForm={onSubmitForm}
        settings={settings}
        state={state}
        extraSettings={extraSettings}
        destinationType={destinationType}
        batchJob={batchJob}
      />
    );
  }
);
